import { AxiosRequestConfig } from 'axios';

import { SessionType } from '../../components/common/Card/UserSessionCard/types';
import { StationsLocationsTypes } from '../../components/features/Map/types';
import axiosInstance from '../../config/axiosInstance';
import { StationTypes } from '../../pages/Station/types';
import { OCPP_API_ENDPOINTS } from './endpoints';
import {
  CalculateResponse,
  ChangeAvailabilityRequest,
  ChangeAvailabilityResponse,
  CreateStationDto,
  DashboardStatsResponse,
  KwhPerDayResponse,
  DiscountUserMetricsTypes,
  GetConfigurationRequest,
  GetConfigurationResponse,
  InvoicePayload,
  ManualSendingRequest,
  ManualSendingResponse,
  PaginationParams,
  SearchParams,
  SessionMetricsResponse,
  SessionResponse,
  SetChargingProfileLimitRequest,
  SetChargingProfileLimitResponse,
  StationLocationParams,
  StationOption,
  StationSearchParams,
  StationsResponse,
  StopTransactionTypes,
  TransactionResponse,
  ActiveSessionsResponse,
  UserSessionResponse,
  IdlePreviewResponse,
} from './types';
import { IHistoryResponse } from '../../pages/UserHistory/types';
import { StationRequestPayload } from '../../components/forms/StationReguestForm/types';

export class OcppService {
  private readonly endpoints = OCPP_API_ENDPOINTS;

  async createInvoice(payload: InvoicePayload): Promise<void> {
    const response = await this.fetchData<{ pageUrl: string }>(
      this.endpoints.PAYMENTS_PATH,
      { method: 'POST', data: payload },
    );
    window.location.href = response.pageUrl;
  }

  async createStationRequest(payload: StationRequestPayload) {
    const response = await this.fetchData(
      this.endpoints.STATION_REQUEST_CREATE_PATH,
      { method: 'POST', data: payload },
    );

    return response;
  }

  async getStationRequests(params: { page: number; pageSize: number; search?: string }) {
    const response = await this.fetchData<{ data: Array<{
      _id: string;
      city: string;
      phone: string;
      comment?: string;
      userId?: { _id: string; email?: string; phone?: string } | string;
      createdAt: string;
    }>; total: number }>(
      this.endpoints.STATION_REQUESTS_ADMIN_PATH,
      {
        method: 'GET',
        params: {
          page: params.page,
          pageSize: params.pageSize,
          address: params.search || undefined,
        },
      },
    );
    return response;
  }

  async getLastUserSession(stationId?: string) {
    const params: { stationId?: string } = {};
    if (stationId) {
      params.stationId = stationId;
    }
    return await this.fetchData<SessionType>(
      this.endpoints.USER_LAST_SESSION_PATH,
      {
        method: 'GET',
        params,
      },
    );
  }

  /** Every charge the account has running right now, newest first. */
  async getActiveSessions(): Promise<UserSessionResponse[]> {
    const response = await this.fetchData<ActiveSessionsResponse>(
      this.endpoints.SESSION_PATH,
      { method: 'GET' },
    );
    return response?.sessions ?? [];
  }

  async getIdlePreview(sessionId: number | string) {
    return await this.fetchData<IdlePreviewResponse>(
      `${this.endpoints.SESSIONS_PATH}/${sessionId}/idle-preview`,
      { method: 'GET' },
    );
  }

  async getStation(id: string): Promise<StationTypes> {
    return await this.fetchData<StationTypes>(
      `${this.endpoints.STATION_PATH}/${id}`,
      { method: 'GET' },
    );
  }

  async getDashboardStations(
    params: PaginationParams & StationLocationParams,
  ): Promise<StationsResponse> {
    return await this.fetchData<StationsResponse>(
      this.endpoints.DASHBOARD_STATIONS_PATH,
      {
        method: 'GET',
        params: this.getPaginationParams(params),
      },
    );
  }

  async getStations(
    params: PaginationParams & StationLocationParams,
  ): Promise<StationsResponse> {
    return await this.fetchData<StationsResponse>(this.endpoints.STATION_PATH, {
      method: 'GET',
      params: this.getPaginationParams(params),
    });
  }

  /**
   * Stations belonging to one organisation (admin-only; `organizationId` is
   * gated server-side). The `address` param is the station list's shared
   * free-text search — it matches address, idTag and numeric station code,
   * not just address.
   */
  async getStationsByOrganization(
    organizationId: string,
    page: number,
    pageSize: number,
    search?: string,
  ): Promise<StationsResponse> {
    return await this.fetchData<StationsResponse>(this.endpoints.STATION_PATH, {
      method: 'GET',
      params: { organizationId, page, pageSize, address: search || undefined },
    });
  }

  /**
   * Create a new charging station
   * @param data - Station creation data
   * @returns WebSocket URL for the created station
   */
  async createStation(data: CreateStationDto): Promise<{ station_url: string }> {
    return this.fetchData<{ station_url: string }>(
      this.endpoints.STATION_CREATE_PATH,
      {
        method: 'POST',
        data,
      },
    );
  }

  async stopTransaction(body: StopTransactionTypes): Promise<void> {
    return await this.fetchData<void>(this.endpoints.STOP_TRANSACTION_PATH, {
      method: 'POST',
      data: body,
    });
  }

  async getTransactions(params: PaginationParams & SearchParams) {
    return await this.fetchData<TransactionResponse>(
      this.endpoints.TRANSACTIONS_PATH,
      {
        method: 'GET',
        params: {
          // `page` arrives already 1-based, like every other list endpoint
          // here — the callers convert from their 0-based table state.
          ...this.getPaginationParams(params),
          userId: params.userId,
        },
      },
    );
  }

  async getUserSessionOrTransactions(url: string, page: number, pageSize: number) {
    return await this.fetchData<IHistoryResponse>(url, {
      method: 'GET',
      params: {
        page,
        pageSize,
      },
    });
  }

  async getSessionMetrics(sessionId: number): Promise<SessionMetricsResponse> {
    return await this.fetchData<SessionMetricsResponse>(
      `${this.endpoints.SESSION_METRICS_PATH}/${sessionId}/metrics`,
      { method: 'GET' },
    );
  }

  async getAllSessions(data: PaginationParams & SearchParams) {
    return await this.fetchData<SessionResponse>(
      this.endpoints.All_SESSIONS_PATH,
      {
        method: 'GET',
        params: {
          // Already 1-based — see getTransactions.
          page: data.page,
          pageSize: data.pageSize,
          sort: data.sort,
          email: data.search?.email,
          address: data.search?.address,
          chargingStatus: data.chargingStatus,
          startDate: data.startDate,
          endDate: data.endDate,
          userId: data.userId,
          stationIdTags: data.stationIdTags,
          maxEnergy: data.maxEnergy,
          maxDurationMinutes: data.maxDurationMinutes,
        },
      },
    );
  }

  async getDiscountUserMetrics(): Promise<DiscountUserMetricsTypes[]> {
    return await this.fetchData<DiscountUserMetricsTypes[]>(
      this.endpoints.GET_DISCOUNT_USER_METRICS_PATH,
      { method: 'GET' },
    );
  }

  async startTransactionWithoutPayments(data: InvoicePayload) {
    return await this.fetchData<void>(
      this.endpoints.START_TRANSACTION_WITHOUT_PAYMENTS_PATH,
      {
        method: 'POST',
        data: {
          idTag: data.idTag,
          connectorId: data.connectorId,
          kwt: data.kwt,
          ...(data.userId ? { userId: data.userId } : {}),
        },
      },
    );
  }

  async calculate(stationId: string, kwt: number, userId?: string) {
    return await this.fetchData<CalculateResponse>(
      `${this.endpoints.CALCULATE_PATH}/${stationId}`,
      {
        method: 'GET',
        params: userId ? { kwt, userId } : { kwt },
      },
    );
  }

  async updateStation(id: string, updatedStationData: Record<string, unknown>) {
    await axiosInstance.patch(
      `${this.endpoints.STATION_PATH}/${id}`,
      updatedStationData,
    );
  }

  async getStationsLocations() {
    return await this.fetchData<StationsLocationsTypes[]>(
      this.endpoints.STATION_LOCATIONS_PATH,
      { method: 'GET' },
    );
  }

  /**
   * Search station locations by address, connector types, and availability filters
   * @param params - Search parameters
   * @returns Array of station locations matching the criteria
   */
  async getStationsLocationsBySearch(
    params: StationSearchParams,
  ): Promise<StationsLocationsTypes[]> {
    return await this.fetchData<StationsLocationsTypes[]>(
      this.endpoints.STATION_LOCATIONS_BY_SEARCH__PATH,
      {
        method: 'GET',
        params,
        paramsSerializer: {
          indexes: false,
        },
      },
    );
  }

  async changeAvailability(payload: ChangeAvailabilityRequest): Promise<ChangeAvailabilityResponse> {
    const response = await this.fetchData<ChangeAvailabilityResponse>(
      this.endpoints.CHANGE_AVAILABILITY_PATH,
      {
        method: 'POST',
        data: payload,
      }
    );
    return response;
  }

  async getConfiguration(payload: GetConfigurationRequest): Promise<GetConfigurationResponse> {
    const response = await this.fetchData<GetConfigurationResponse>(
      this.endpoints.GET_CONFIGURATION_PATH,
      {
        method: 'POST',
        data: payload,
      }
    );
    return response;
  }

  /**
   * Send manual OCPP message to station
   * @param payload - Manual sending request
   * @returns Response with success status and message
   */
  async manualSending(payload: ManualSendingRequest): Promise<ManualSendingResponse> {
    return this.fetchData<ManualSendingResponse>(
      this.endpoints.MANUAL_SENDING_PATH,
      {
        method: 'POST',
        data: payload,
      },
    );
  }

  async setChargingProfileLimit(
    payload: SetChargingProfileLimitRequest,
  ): Promise<SetChargingProfileLimitResponse> {
    return this.fetchData<SetChargingProfileLimitResponse>(
      this.endpoints.SET_CHARGING_PROFILE_LIMIT_PATH,
      { method: 'POST', data: payload },
    );
  }

  async getInvoiceStatus(invoiceId: string): Promise<Record<string, unknown>> {
    return await this.fetchData<Record<string, unknown>>(
      this.endpoints.INVOICE_STATUS_PATH,
      {
        method: 'GET',
        params: { invoiceId },
      }
    );
  }

  /**
   * Get dashboard stats (total kWh, transaction amount, user count)
   * @param params - Optional date range and station filter
   */
  async getDashboardStats(params?: {
    startDate?: string;
    endDate?: string;
    stationIds?: string;
    organizationIds?: string;
  }): Promise<DashboardStatsResponse> {
    return this.fetchData<DashboardStatsResponse>(
      this.endpoints.DASHBOARD_STATS_PATH,
      { method: 'GET', params },
    );
  }

  /**
   * Get kWh delivered per day as a zero-filled time series.
   * @param params - Date range (required for results), optional station filter and timezone
   */
  async getKwhPerDay(params?: {
    startDate?: string;
    endDate?: string;
    stationIds?: string;
    organizationIds?: string;
    timezone?: string;
  }): Promise<KwhPerDayResponse> {
    return this.fetchData<KwhPerDayResponse>(
      this.endpoints.DASHBOARD_KWH_PER_DAY_PATH,
      { method: 'GET', params },
    );
  }

  /**
   * Get stations list for selection/autocomplete
   * @param search - Optional search string
   */
  async getStationsList(search?: string, organizationIds?: string): Promise<StationOption[]> {
    return this.fetchData<StationOption[]>(
      this.endpoints.DASHBOARD_STATIONS_LIST_PATH,
      { method: 'GET', params: { search, organizationIds } },
    );
  }

  private async fetchData<T>(
    endpoint: string,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    // The response interceptor unwraps `response.data`, so the awaited value is
    // already the payload — axios' own return type still describes AxiosResponse.
    const response = await axiosInstance(endpoint, config);
    return response as T;
  }

  private getPaginationParams(params: PaginationParams & SearchParams) {
    const { page, pageSize, sort, ...rest } = params;
    return {
      page,
      pageSize,
      sort,
      ...rest,
    };
  }
}

// Singleton instance - avoids creating multiple service instances
export const ocppService = new OcppService();
